import React, { useState, useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Typography,
} from "@mui/material";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import DateRangeFilter from "../components/common/DateRangeFilter";
import AppCircularProgress from "../components/common/AppCircularProgress";

import { getBiInsights } from "../redux/actions/biActions";

// --- Import All BI Widgets ---
import SalesTrendsWidget from "../components/bi/widgets/SalesTrendsWidget";
import ProcurementTrendsWidget from "../components/bi/widgets/ProcurementTrendsWidget";
import TopBranchesWidget from "../components/bi/widgets/TopBranchesWidget";
import TopProductsWidget from "../components/bi/widgets/TopProductsWidget";
import CategoryPerformanceWidget from "../components/bi/widgets/CategoryPerformanceWidget";
import BrandPerformanceWidget from "../components/bi/widgets/BrandPerformanceWidget";
import TopSalesRepsWidget from "../components/bi/widgets/TopSalesRepsWidget";
import QuoteConversionFunnelWidget from "../components/bi/widgets/QuoteConversionFunnelWidget";
import ExpenseBreakdownWidget from "../components/bi/widgets/ExpenseBreakdownWidget";
import InventoryMovementWidget from "../components/bi/widgets/InventoryMovementWidget";
import CreditNoteReasonsWidget from "../components/bi/widgets/CreditNoteReasonsWidget";
import TopB2BCustomersWidget from "../components/bi/widgets/TopB2BCustomersWidget";
import TopDebtorsWidget from "../components/bi/widgets/TopDebtorsWidget";
import TopSuppliersWidget from "../components/bi/widgets/TopSuppliersWidget";
import PaymentPreferencesWidget from "../components/bi/widgets/PaymentPreferencesWidget";
import { BiEnum } from "../shared/enums/BiEnum";

// 1. WIDGET REGISTRY: Maps the API Key to its Component & Layout Size
type WidgetSizes = {
  xs: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
};
type WidgetConfig = { component: React.ElementType; sizes: WidgetSizes };

// 1. WIDGET REGISTRY: Maps the BI Enum to its Component & Layout Size
const WIDGET_REGISTRY: Record<BiEnum, WidgetConfig> = {
  [BiEnum.SALES_TRENDS]: {
    component: SalesTrendsWidget,
    sizes: { xs: 12, lg: 6 },
  },
  [BiEnum.PROCUREMENT_TRENDS]: {
    component: ProcurementTrendsWidget,
    sizes: { xs: 12, lg: 6 },
  },
  [BiEnum.TOP_BRANCHES]: { component: TopBranchesWidget, sizes: { xs: 12 } },
  [BiEnum.TOP_PRODUCTS]: { component: TopProductsWidget, sizes: { xs: 12 } },
  [BiEnum.CATEGORY_PERFORMANCE]: {
    component: CategoryPerformanceWidget,
    sizes: { xs: 12, md: 6 },
  },
  [BiEnum.BRAND_PERFORMANCE]: {
    component: BrandPerformanceWidget,
    sizes: { xs: 12, md: 6 },
  },
  [BiEnum.TOP_SALES_REP]: {
    component: TopSalesRepsWidget,
    sizes: { xs: 12, md: 6 },
  },
  [BiEnum.QUOTE_CONVERSION_FUNNEL]: {
    component: QuoteConversionFunnelWidget,
    sizes: { xs: 12, md: 6 },
  },
  [BiEnum.EXPENSE_BREAKDOWN]: {
    component: ExpenseBreakdownWidget,
    sizes: { xs: 12, md: 4 },
  },
  [BiEnum.INVENTORY_MOVEMENT]: {
    component: InventoryMovementWidget,
    sizes: { xs: 12, md: 4 },
  },
  [BiEnum.CREDIT_NOTE_REASONS]: {
    component: CreditNoteReasonsWidget,
    sizes: { xs: 12, md: 4 },
  },
  [BiEnum.TOP_B2B_CUSTOMERS]: {
    component: TopB2BCustomersWidget,
    sizes: { xs: 12, md: 6 },
  },
  [BiEnum.TOP_DEBTORS]: {
    component: TopDebtorsWidget,
    sizes: { xs: 12, md: 6 },
  },
  [BiEnum.TOP_SUPPLIERS]: {
    component: TopSuppliersWidget,
    sizes: { xs: 12, md: 6 },
  },
  [BiEnum.PAYMENT_PREFERENCES]: {
    component: PaymentPreferencesWidget,
    sizes: { xs: 12, md: 6 },
  },
};

// 2. ORDERED KEYS: Ensures widgets always render in a logical, structured flow
const ORDERED_WIDGET_KEYS: BiEnum[] = [
  BiEnum.SALES_TRENDS,
  BiEnum.PROCUREMENT_TRENDS,
  BiEnum.TOP_BRANCHES,
  BiEnum.TOP_PRODUCTS,
  BiEnum.CATEGORY_PERFORMANCE,
  BiEnum.BRAND_PERFORMANCE,
  BiEnum.TOP_SALES_REP,
  BiEnum.QUOTE_CONVERSION_FUNNEL,
  BiEnum.EXPENSE_BREAKDOWN,
  BiEnum.INVENTORY_MOVEMENT,
  BiEnum.CREDIT_NOTE_REASONS,
  BiEnum.TOP_B2B_CUSTOMERS,
  BiEnum.TOP_DEBTORS,
  BiEnum.TOP_SUPPLIERS,
  BiEnum.PAYMENT_PREFERENCES,
];

const BiPage: React.FC = React.memo(() => {
  const dispatch = useAppDispatch();

  const { insights, loadingInsights, errorLoadingInsights } = useAppSelector(
    (state) => state.bi,
  );

  // Time Range Logic
  const today = new Date().toISOString().split("T")[0];
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const defaultStartDate = thirtyDaysAgo.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(today);
  const [recordLimit, setRecordLimit] = useState(10);

  const fetchInsights = useCallback(() => {
    dispatch(
      getBiInsights({
        startDate,
        endDate,
        limit: 10,
        recordLimit,
      }),
    );
  }, [dispatch, startDate, endDate, recordLimit]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  // Determine which widgets to render based on API response
  const activeKeys = insights ? Object.keys(insights) : [];

  return (
    <PageContainer
      title="Business Intelligence"
      subtitle="Comprehensive data analytics, trends, and business performance metrics."
    >
      <PageToolbar hideSearch={true}>
        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Top Records</InputLabel>
          <Select
            value={recordLimit}
            onChange={(e) => setRecordLimit(e.target.value)}
            label="Top Records"
          >
            <MenuItem value={5}>Top 5</MenuItem>
            <MenuItem value={10}>Top 10</MenuItem>
            <MenuItem value={20}>Top 20</MenuItem>
            <MenuItem value={50}>Top 50</MenuItem>
          </Select>
        </FormControl>
      </PageToolbar>

      {errorLoadingInsights && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorLoadingInsights}
        </Alert>
      )}

      {loadingInsights && !insights ? (
        <AppCircularProgress />
      ) : (
        <Grid container spacing={3}>
          {/* Dynamic Rendering Block */}
          {ORDERED_WIDGET_KEYS.map((key) => {
            // Check if the API returned this specific key
            if (!activeKeys.includes(key)) return null;

            const widgetConfig = WIDGET_REGISTRY[key];
            if (!widgetConfig) return null; // Safe fallback

            const WidgetComponent = widgetConfig.component;
            const sizes = widgetConfig.sizes;

            return (
              <Grid
                size={{
                  xs: sizes.xs,
                  sm: sizes.sm,
                  md: sizes.md,
                  lg: sizes.lg,
                  xl: sizes.xl,
                }}
                key={key}
              >
                <WidgetComponent data={insights![key]} />
              </Grid>
            );
          })}

          {/* Empty State if API returns completely empty insights object */}
          {!loadingInsights && activeKeys.length === 0 && (
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  textAlign: "center",
                  py: 5,
                }}
              >
                <Typography color="text.secondary">
                  No business intelligence data available for the selected
                  period.
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </PageContainer>
  );
});

BiPage.displayName = "BiPage";
export default BiPage;
